import { z } from "zod";
import { CURRENCY_CODES } from "../money/currency.ts";
import { FEATURE_CODES } from "../catalog/features.ts";
import { QUOTA_RESOURCES, QuotaAllowanceSchema } from "../catalog/quotas.ts";

/**
 * Contracts for `/api/admin/**` — the operator realm.
 *
 * Money crosses this boundary as a **decimal string**, never a JSON number.
 * Amounts are `bigint` minor units in the database and a JSON number would
 * silently round anything past 2^53; a string round-trips exactly, and the
 * client parses it back with the currency's own exponent.
 */

export const MoneySchema = z.object({
  amount: z.string().regex(/^-?\d+$/, "minor units as a decimal string"),
  currency: z.enum(CURRENCY_CODES),
});
export type MoneyDto = z.infer<typeof MoneySchema>;

export const BillingScopeSchema = z.enum(["store", "chain"]);
export const BillingCycleSchema = z.enum(["monthly", "yearly"]);

export const SubscriptionStatusSchema = z.enum([
  "trialing",
  "normal",
  "expired",
  "invalidated",
  "cancelled",
]);

/**
 * A merchant's own state, which is not its subscription's: an active merchant
 * whose subscription lapsed is `active` here and `expired` there. Named once so
 * the list filter offers exactly the words the column renders.
 */
export const ChainStatusSchema = z.enum(["active", "suspended", "invalidated"]);

// ── Plan catalog ────────────────────────────────────────────────────────────

export const PlanQuotaSchema = z.object({
  resource: z.enum(QUOTA_RESOURCES),
  /** Null is unmetered. */
  limitValue: z.number().int().nonnegative().nullable(),
  overagePricePerMonth: z.string().regex(/^\d+$/).default("0"),
});

export const PlanFeatureSchema = z.object({
  featureCode: z.enum(FEATURE_CODES),
  grade: z.string().nullable().default(null),
  isPermanent: z.boolean().default(false),
});

export const PlanVersionSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  tier: z.number().int(),
  country: z.string(),
  currency: z.enum(CURRENCY_CODES),
  pricePerMonth: z.string(),
  originalPricePerMonth: z.string(),
  yearlyPricePerMonth: z.string(),
  /** Derived for display; never stored. */
  annualSavingBp: z.number().int(),
  isPermanentAvailable: z.boolean(),
  permanentMonths: z.number().int(),
  pilotOnly: z.boolean(),
  status: z.enum(["active", "invalidated"]),
  sort: z.number().int(),
  quotas: z.array(PlanQuotaSchema),
  features: z.array(PlanFeatureSchema),
});
export type PlanVersion = z.infer<typeof PlanVersionSchema>;

export const CreatePlanVersionSchema = z
  .object({
    code: z
      .string()
      .min(1)
      .max(40)
      .regex(/^[A-Z][A-Z0-9_]*$/, "upper snake case"),
    name: z.string().min(1),
    description: z.string().nullable().default(null),
    tier: z.number().int().min(1).max(100),
    country: z.string().length(2),
    currency: z.enum(CURRENCY_CODES),
    pricePerMonth: z.string().regex(/^\d+$/),
    originalPricePerMonth: z.string().regex(/^\d+$/),
    yearlyPricePerMonth: z.string().regex(/^\d+$/),
    isPermanentAvailable: z.boolean().default(false),
    permanentMonths: z.number().int().min(1).max(600).default(60),
    pilotOnly: z.boolean().default(false),
    sort: z.number().int().default(0),
    quotas: z.array(PlanQuotaSchema).default([]),
    features: z.array(PlanFeatureSchema).default([]),
  })
  .refine((v) => BigInt(v.originalPricePerMonth) >= BigInt(v.pricePerMonth), {
    // The source enforces this: a struck-through price below the real one is a
    // false saving on the pricing page.
    message: "The original price must be at least the current price",
    path: ["originalPricePerMonth"],
  })
  .refine((v) => BigInt(v.yearlyPricePerMonth) <= BigInt(v.pricePerMonth), {
    message: "The yearly rate must not exceed the monthly rate",
    path: ["yearlyPricePerMonth"],
  });
export type CreatePlanVersion = z.infer<typeof CreatePlanVersionSchema>;

export const UpdatePlanVersionSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  pricePerMonth: z.string().regex(/^\d+$/).optional(),
  originalPricePerMonth: z.string().regex(/^\d+$/).optional(),
  yearlyPricePerMonth: z.string().regex(/^\d+$/).optional(),
  isPermanentAvailable: z.boolean().optional(),
  permanentMonths: z.number().int().min(1).max(600).optional(),
  pilotOnly: z.boolean().optional(),
  status: z.enum(["active", "invalidated"]).optional(),
  sort: z.number().int().optional(),
  quotas: z.array(PlanQuotaSchema).optional(),
  features: z.array(PlanFeatureSchema).optional(),
});

// ── Features and hardware ───────────────────────────────────────────────────

export const AdminFeatureSchema = z.object({
  id: z.uuid(),
  code: z.enum(FEATURE_CODES),
  name: z.string(),
  description: z.string().nullable(),
  group: z.string(),
  isValueAdded: z.boolean(),
  pricePerMonth: z.string(),
  currency: z.enum(CURRENCY_CODES),
  isPermanentOption: z.boolean(),
  status: z.enum(["active", "invalidated"]),
  grades: z.array(z.string()).nullable(),
});

export const UpdateFeatureSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  isValueAdded: z.boolean().optional(),
  pricePerMonth: z.string().regex(/^\d+$/).optional(),
  isPermanentOption: z.boolean().optional(),
  status: z.enum(["active", "invalidated"]).optional(),
});

export const HardwareSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  model: z.string().nullable(),
  description: z.string().nullable(),
  price: z.string(),
  currency: z.enum(CURRENCY_CODES),
  status: z.enum(["active", "invalidated"]),
  sort: z.number().int(),
});

export const CreateHardwareSchema = z.object({
  code: z.string().min(1).max(40),
  name: z.string().min(1),
  model: z.string().nullable().default(null),
  description: z.string().nullable().default(null),
  price: z.string().regex(/^\d+$/).default("0"),
  currency: z.enum(CURRENCY_CODES).default("USD"),
  sort: z.number().int().default(0),
});

/**
 * Spelled out rather than derived with `.partial()`.
 *
 * `.partial()` makes a field optional but **keeps its `.default()`**, so a
 * patch that omits a key still parses to the default and overwrites whatever
 * was there. A caller changing only a price would silently reset every other
 * defaulted column. Patch schemas therefore carry no defaults at all.
 */
export const UpdateHardwareSchema = z.object({
  code: z.string().min(1).max(40).optional(),
  name: z.string().min(1).optional(),
  model: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  price: z.string().regex(/^\d+$/).optional(),
  currency: z.enum(CURRENCY_CODES).optional(),
  sort: z.number().int().optional(),
  status: z.enum(["active", "invalidated"]).optional(),
});

// ── Market profiles ─────────────────────────────────────────────────────────

/**
 * The markets a merchant can be created in (`03-data-model.md` § 10.1).
 *
 * Read-only, and that is the whole point of it. Without a profile a store has
 * no currency, tax template, rounding or locales, so `POST /api/admin/merchants`
 * refuses the country outright — which makes a console that offers a country
 * with no profile a console that offers a failure. The list is the rows.
 *
 * `name` is the row's own, because a country name is data here and nowhere
 * else. Deliberately not published: `cashRoundingIncrement` (a `bigint` nothing
 * on this screen renders), `minorUnit` (no money is formatted from this), and
 * the tax and inventory templates, which are provisioning internals — a client
 * reasoning about tax from a dropdown endpoint is `pos-core`'s job taken away
 * from it.
 */
export const MarketProfileSummarySchema = z.object({
  /** ISO 3166-1 alpha-2 — the value `CreateMerchantSchema.country` takes. */
  code: z.string().length(2),
  name: z.string(),
  /** What the first store is born trading in. */
  defaultCurrency: z.enum(CURRENCY_CODES),
  secondCurrency: z.enum(CURRENCY_CODES).nullable(),
  /** The language packs the first store starts with. */
  defaultLocales: z.array(z.string()),
});
export type MarketProfileSummary = z.infer<typeof MarketProfileSummarySchema>;

// ── Merchants ───────────────────────────────────────────────────────────────

export const MerchantSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  country: z.string(),
  status: ChainStatusSchema,
  isPilot: z.boolean(),
  channelPartnerId: z.uuid().nullable(),
  channelPartnerName: z.string().nullable(),
  bdStaffId: z.uuid().nullable(),
  bdStaffName: z.string().nullable(),
  afterSalesStaffId: z.uuid().nullable(),
  afterSalesStaffName: z.string().nullable(),
  storeCount: z.number().int(),
  planVersionCode: z.string().nullable(),
  planVersionName: z.string().nullable(),
  subscriptionStatus: SubscriptionStatusSchema.nullable(),
  expirationTime: z.string().nullable(),
  createdAt: z.string(),
});
export type MerchantSummary = z.infer<typeof MerchantSummarySchema>;

export const CreateMerchantSchema = z.object({
  name: z.string().min(1).max(120),
  country: z.string().length(2),
  settlementTimezone: z.string().default("UTC"),
  channelPartnerId: z.uuid().nullable().default(null),
  bdStaffId: z.uuid().nullable().default(null),
  afterSalesStaffId: z.uuid().nullable().default(null),
  isPilot: z.boolean().default(false),
  internalRemarks: z.string().nullable().default(null),
  /** The first location. A merchant with no store cannot trade. */
  store: z.object({
    name: z.string().min(1).max(120),
    slug: z
      .string()
      .min(2)
      .max(60)
      .regex(/^[a-z0-9][a-z0-9-]*$/, "lower kebab case"),
    address: z.string().nullable().default(null),
    phone: z.string().nullable().default(null),
    timezone: z.string().default("UTC"),
  }),
  owner: z.object({
    username: z.string().min(2).max(60),
    displayName: z.string().min(1).max(120),
    email: z.email(),
    password: z.string().min(12),
  }),
});
export type CreateMerchant = z.infer<typeof CreateMerchantSchema>;

export const UpdateMerchantSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  channelPartnerId: z.uuid().nullable().optional(),
  bdStaffId: z.uuid().nullable().optional(),
  afterSalesStaffId: z.uuid().nullable().optional(),
  isPilot: z.boolean().optional(),
  internalRemarks: z.string().nullable().optional(),
  settlementTimezone: z.string().optional(),
  status: z.enum(["active", "suspended"]).optional(),
});

export const MerchantDetailSchema = MerchantSummarySchema.extend({
  settlementTimezone: z.string(),
  internalRemarks: z.string().nullable(),
  stores: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      slug: z.string(),
      isMain: z.boolean(),
      status: z.string(),
      primaryCurrency: z.string(),
    }),
  ),
  users: z.array(
    z.object({
      id: z.uuid(),
      username: z.string(),
      displayName: z.string(),
      email: z.string().nullable(),
      status: z.string(),
    }),
  ),
});

// ── Contracts ───────────────────────────────────────────────────────────────

export const ContractExtraQuotaSchema = z.object({
  resource: z.enum(QUOTA_RESOURCES),
  quantity: z.number().int().min(0),
  amount: z.string().regex(/^\d+$/).default("0"),
});

export const SignContractSchema = z
  .object({
    chainId: z.uuid(),
    scope: BillingScopeSchema.default("store"),
    /** Required when `scope` is `store`. */
    storeId: z.uuid().nullable().default(null),
    planVersionId: z.uuid(),
    signDate: z.iso.datetime(),
    durationMonths: z.number().int().min(1).max(120),
    subscriptionStart: z.iso.datetime(),
    cycle: BillingCycleSchema.default("monthly"),
    isPermanent: z.boolean().default(false),
    hardwareIncluded: z.boolean().default(false),
    hardwareAmount: z.string().regex(/^\d+$/).default("0"),
    addonFeatures: z.array(z.enum(FEATURE_CODES)).default([]),
    extraQuotas: z.array(ContractExtraQuotaSchema).default([]),
    discountAmount: z.string().regex(/^\d+$/).default("0"),
    freeTrialMonths: z.number().int().min(0).max(24).default(0),
    giftMonths: z.number().int().min(0).max(24).default(0),
    bdStaffId: z.uuid().nullable().default(null),
    afterSalesStaffId: z.uuid().nullable().default(null),
    source: z.string().nullable().default(null),
    documentUrl: z.string().nullable().default(null),
    internalRemarks: z.string().nullable().default(null),
  })
  .refine((v) => new Date(v.subscriptionStart) >= new Date(v.signDate), {
    // The source enforces this: a subscription cannot start before the contract
    // that created it was signed.
    message: "The subscription cannot start before the contract is signed",
    path: ["subscriptionStart"],
  })
  .refine((v) => v.scope === "chain" || v.storeId !== null, {
    message: "A store-scoped contract must name a store",
    path: ["storeId"],
  });
export type SignContract = z.infer<typeof SignContractSchema>;

export const ContractSummarySchema = z.object({
  id: z.uuid(),
  contractNo: z.string(),
  chainId: z.uuid(),
  merchantName: z.string(),
  scope: BillingScopeSchema,
  storeId: z.uuid().nullable(),
  planVersionCode: z.string(),
  planVersionName: z.string(),
  signDate: z.string(),
  durationMonths: z.number().int(),
  subscriptionStart: z.string(),
  subscriptionEnd: z.string(),
  currency: z.enum(CURRENCY_CODES),
  versionAmount: z.string(),
  addonAmount: z.string(),
  hardwareAmount: z.string(),
  discountAmount: z.string(),
  total: z.string(),
  status: z.enum(["draft", "submitted", "active", "completed", "cancelled"]),
  submittedAt: z.string().nullable(),
  createdAt: z.string(),
});

export const ContractDetailSchema = ContractSummarySchema.extend({
  hardwareIncluded: z.boolean(),
  freeTrialMonths: z.number().int(),
  giftMonths: z.number().int(),
  bdStaffName: z.string().nullable(),
  afterSalesStaffName: z.string().nullable(),
  source: z.string().nullable(),
  documentUrl: z.string().nullable(),
  internalRemarks: z.string().nullable(),
  extraQuotas: z.array(ContractExtraQuotaSchema),
  segments: z.array(
    z.object({
      sequence: z.number().int(),
      kind: z.enum(["initial", "trial", "renewal", "upgrade", "addon"]),
      planVersionCode: z.string(),
      startsAt: z.string(),
      endsAt: z.string(),
      amount: z.string(),
      note: z.string().nullable(),
    }),
  ),
});

// ── Subscriptions ───────────────────────────────────────────────────────────

export const SubscriptionSchema = z.object({
  id: z.uuid(),
  chainId: z.uuid(),
  merchantName: z.string(),
  scope: BillingScopeSchema,
  storeId: z.uuid().nullable(),
  planVersionId: z.uuid(),
  planVersionCode: z.string(),
  planVersionName: z.string(),
  tierSnapshot: z.number().int(),
  status: SubscriptionStatusSchema,
  /** Computed from the dates rather than read from the column. */
  effectiveStatus: SubscriptionStatusSchema,
  signTime: z.string(),
  startTime: z.string(),
  expirationTime: z.string().nullable(),
  trialEndsAt: z.string().nullable(),
  isPermanent: z.boolean(),
  signMonths: z.number().int(),
  currency: z.enum(CURRENCY_CODES),
  priceSnapshot: z.string(),
  quotaSnapshot: z.record(z.string(), QuotaAllowanceSchema),
  featureSnapshot: z.array(
    z.object({ code: z.string(), grade: z.string().nullable(), isPermanent: z.boolean() }),
  ),
  addons: z.array(
    z.object({
      id: z.uuid(),
      kind: z.enum(["feature", "quota"]),
      featureCode: z.string().nullable(),
      grade: z.string().nullable(),
      resource: z.string().nullable(),
      quantity: z.number().int(),
      startTime: z.string(),
      endTime: z.string().nullable(),
      isPermanent: z.boolean(),
      amount: z.string(),
    }),
  ),
});

export const RenewSubscriptionSchema = z.object({
  months: z.number().int().min(1).max(120),
  cycle: BillingCycleSchema.default("monthly"),
  /** Renewing onto a different version. Never a lower tier. */
  planVersionId: z.uuid().optional(),
  discountAmount: z.string().regex(/^\d+$/).default("0"),
});

export const ChangeVersionSchema = z.object({
  planVersionId: z.uuid(),
  cycle: BillingCycleSchema.default("monthly"),
  effectiveAt: z.iso.datetime().optional(),
});

export const PurchaseAddonSchema = z
  .object({
    kind: z.enum(["feature", "quota"]),
    featureCode: z.enum(FEATURE_CODES).optional(),
    grade: z.string().optional(),
    resource: z.enum(QUOTA_RESOURCES).optional(),
    quantity: z.number().int().min(1).optional(),
    isPermanent: z.boolean().default(false),
  })
  .refine((v) => v.kind !== "feature" || v.featureCode !== undefined, {
    message: "A feature add-on must name a feature",
    path: ["featureCode"],
  })
  .refine((v) => v.kind !== "quota" || (v.resource !== undefined && v.quantity !== undefined), {
    message: "A quota add-on must name a resource and a quantity",
    path: ["resource"],
  });

export const ProrationQuoteSchema = z.object({
  charged: z.string(),
  credit: z.string(),
  net: z.string(),
  currency: z.enum(CURRENCY_CODES),
  chargedDays: z.number().int(),
  termDays: z.number().int(),
});

export const ActivateTrialSchema = z.object({
  planVersionId: z.uuid(),
  months: z.number().int().min(1).max(12).default(1),
});

export const InvalidateSubscriptionSchema = z.object({
  /** Required and typed by hand; the source warns the action is irreversible. */
  reason: z.string().min(10).max(500),
  otpChallengeId: z.uuid(),
  otpCode: z.string().min(4).max(10),
});

// ── OTP ─────────────────────────────────────────────────────────────────────

export const RequestOtpSchema = z.object({
  purpose: z.enum(["invalidate_subscription", "complete_settlement"]),
  subjectId: z.uuid(),
});

export const OtpChallengeSchema = z.object({
  id: z.uuid(),
  purpose: z.enum(["invalidate_subscription", "complete_settlement"]),
  expiresAt: z.string(),
  /** Development only, so an unattended run can complete the flow. */
  devCode: z.string().optional(),
});

// ── Partners and settlement ─────────────────────────────────────────────────

export const ChannelPartnerSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  country: z.string().nullable(),
  commissionRateBp: z.number().int(),
  firstSignBonus: z.string(),
  settlementMethod: z.string().nullable(),
  settlementTimezone: z.string(),
  transactionCurrency: z.enum(CURRENCY_CODES),
  settlementCurrency: z.enum(CURRENCY_CODES),
  status: z.enum(["active", "suspended"]),
  merchantCount: z.number().int(),
});

export const CreateChannelPartnerSchema = z.object({
  code: z.string().min(2).max(40),
  name: z.string().min(1).max(120),
  country: z.string().length(2).nullable().default(null),
  commissionRateBp: z.number().int().min(0).max(10_000).default(0),
  firstSignBonus: z.string().regex(/^\d+$/).default("0"),
  settlementMethod: z.string().nullable().default(null),
  settlementTimezone: z.string().default("UTC"),
  transactionCurrency: z.enum(CURRENCY_CODES).default("USD"),
  settlementCurrency: z.enum(CURRENCY_CODES).default("USD"),
});

/** No defaults, for the reason given on `UpdateHardwareSchema`. */
export const UpdateChannelPartnerSchema = z.object({
  code: z.string().min(2).max(40).optional(),
  name: z.string().min(1).max(120).optional(),
  country: z.string().length(2).nullable().optional(),
  commissionRateBp: z.number().int().min(0).max(10_000).optional(),
  firstSignBonus: z.string().regex(/^\d+$/).optional(),
  settlementMethod: z.string().nullable().optional(),
  settlementTimezone: z.string().optional(),
  transactionCurrency: z.enum(CURRENCY_CODES).optional(),
  settlementCurrency: z.enum(CURRENCY_CODES).optional(),
  status: z.enum(["active", "suspended"]).optional(),
});

export const SettlementSchema = z.object({
  id: z.uuid(),
  settlementNo: z.string(),
  channelPartnerId: z.uuid(),
  channelPartnerName: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  transactionCount: z.number().int(),
  transactionCurrency: z.enum(CURRENCY_CODES),
  transactionAmount: z.string(),
  commissionRateBpSnapshot: z.number().int(),
  commissionDue: z.string(),
  firstSignBonus: z.string(),
  settlementCurrency: z.enum(CURRENCY_CODES),
  exchangeRate: z.string(),
  settleAmountDue: z.string(),
  actualSettleAmount: z.string(),
  method: z.string().nullable(),
  proofUrl: z.string().nullable(),
  auditorId: z.uuid().nullable(),
  auditorName: z.string().nullable(),
  completedAt: z.string().nullable(),
  status: z.enum(["draft", "pending_audit", "completed", "failed"]),
  reconciled: z.boolean(),
});

export const PrepareSettlementSchema = z.object({
  channelPartnerId: z.uuid(),
  periodStart: z.iso.datetime(),
  periodEnd: z.iso.datetime(),
  exchangeRate: z.string().regex(/^\d+$/).default("1000000"),
});

export const CompleteSettlementSchema = z.object({
  actualSettleAmount: z.string().regex(/^\d+$/),
  method: z.string().min(1),
  proofUrl: z.string().nullable().default(null),
  settlementPersonnel: z.string().min(1),
  settlementPersonnelIdNumber: z.string().nullable().default(null),
  /** Maker/checker: the source requires an auditor to be nominated. */
  auditorId: z.uuid(),
  otpChallengeId: z.uuid(),
  otpCode: z.string().min(4).max(10),
});

// ── Financial ───────────────────────────────────────────────────────────────

export const SubscriptionTransactionSchema = z.object({
  id: z.uuid(),
  serialNo: z.string(),
  chainId: z.uuid(),
  merchantName: z.string(),
  productKind: z.enum(["plan", "addon", "hardware", "package"]),
  productRef: z.string().nullable(),
  description: z.string().nullable(),
  currency: z.enum(CURRENCY_CODES),
  minorUnit: z.number().int(),
  amount: z.string(),
  method: z.string().nullable(),
  proofUrl: z.string().nullable(),
  paidAt: z.string(),
  settlementStatus: z.enum(["pending", "settled"]),
  reconciled: z.boolean(),
  reconciledAt: z.string().nullable(),
});

export const ReconcileSchema = z.object({
  reconciled: z.boolean(),
  proofUrl: z.string().nullable().default(null),
  note: z.string().nullable().default(null),
});

// ── Dashboard ───────────────────────────────────────────────────────────────

/**
 * The operator dashboard. The source states these formulas verbatim, and the
 * vocabulary separation is deliberate: a "transaction" here is a subscription
 * payment, not a restaurant sale (`06-saas-admin.md` § 1).
 */
export const DashboardSchema = z.object({
  period: z.object({ start: z.string(), end: z.string() }),
  merchants: z.object({
    new: z.number().int(),
    cumulative: z.number().int(),
    active: z.number().int(),
    /** Active ÷ total × 100, in basis points to avoid a float. */
    activeRateBp: z.number().int(),
    /** (current − previous) ÷ previous × 100, in basis points. Null when there is no previous. */
    monthOnMonthGrowthBp: z.number().int().nullable(),
  }),
  subscriptionRevenue: z.object({
    currency: z.enum(CURRENCY_CODES),
    transactionCount: z.number().int(),
    transactionAmount: z.string(),
    pendingSettlementAmount: z.string(),
    settledAmount: z.string(),
    totalCumulativeRevenue: z.string(),
  }),
});

// ── Team and referrals ──────────────────────────────────────────────────────

export const OperatorUserSchema = z.object({
  id: z.uuid(),
  username: z.string(),
  displayName: z.string(),
  email: z.string().nullable(),
  roleId: z.uuid().nullable(),
  roleCode: z.string().nullable(),
  roleName: z.string().nullable(),
  channelPartnerId: z.uuid().nullable(),
  status: z.string(),
});
export type OperatorUser = z.infer<typeof OperatorUserSchema>;

export const CreateOperatorUserSchema = z.object({
  username: z.string().min(2).max(60),
  displayName: z.string().min(1).max(120),
  email: z.email(),
  password: z.string().min(12),
  roleId: z.uuid(),
  channelPartnerId: z.uuid().nullable().default(null),
});

export const UpdateOperatorUserSchema = z.object({
  displayName: z.string().min(1).max(120).optional(),
  roleId: z.uuid().optional(),
  channelPartnerId: z.uuid().nullable().optional(),
  status: z.enum(["active", "disabled"]).optional(),
});

export const OperatorRoleSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isSystem: z.boolean(),
  permissions: z.array(z.string()),
  memberCount: z.number().int(),
});
export type OperatorRole = z.infer<typeof OperatorRoleSchema>;

export const SaveOperatorRoleSchema = z.object({
  code: z
    .string()
    .min(2)
    .max(40)
    .regex(/^[A-Z][A-Z0-9_]*$/)
    .optional(),
  name: z.string().min(1).max(80),
  description: z.string().nullable().default(null),
  // A repeated key is not a mistake worth refusing — `core_role_permissions_uq`
  // enforces one row per (roleId, permissionKey), so a duplicate that reached
  // the insert unique-violated and read back as "name or code already exists",
  // which sent the reader looking at the wrong fields.
  permissions: z
    .array(z.string())
    .min(1)
    .transform((keys) => [...new Set(keys)]),
});

/**
 * Derived rather than restating `min(12)`, so a floor raised at creation
 * cannot leave a reset accepting what the create would refuse.
 */
export const SetOperatorPasswordSchema = CreateOperatorUserSchema.pick({ password: true });

export const ReferralSchema = z.object({
  id: z.uuid(),
  storeId: z.uuid().nullable(),
  storeName: z.string().nullable(),
  submittedByName: z.string().nullable(),
  businessName: z.string(),
  contactName: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  country: z.string().nullable(),
  note: z.string().nullable(),
  status: z.enum(["new", "contacted", "converted", "rejected"]),
  ownerId: z.uuid().nullable(),
  createdAt: z.string(),
});

export const UpdateReferralSchema = z.object({
  status: z.enum(["new", "contacted", "converted", "rejected"]).optional(),
  ownerId: z.uuid().nullable().optional(),
  note: z.string().nullable().optional(),
});

export const AuditEntrySchema = z.object({
  id: z.uuid(),
  actorName: z.string(),
  action: z.string(),
  entity: z.string(),
  entityId: z.uuid().nullable(),
  changedFields: z.array(z.string()),
  createdAt: z.string(),
});
